import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

export const siteNavigationFocusRing = {
  outline: '2px solid',
  outlineColor: 'brand.solid',
  outlineOffset: '-2px',
}

export enum SiteNavigationTabSizes {
  Normal = 'normal',
  Collapsed = 'collapsed',
  CollapsedBadged = 'collapsedBadged',
}

const SiteNavigationTabParts = [
  'container',
  'selectionContainer',
  'contentWrapper',
  'iconWrapper',
  'icon',
  'titleText',
  'notificationBadge',
  'statusBadge',
] as const

const helpers = createMultiStyleConfigHelpers(SiteNavigationTabParts)

export const SiteNavigationTabStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      gap: '0.125rem',
    },
    selectionContainer: {
      display: 'flex',
      height: '3rem',
      py: '0.625rem',
      userSelect: 'none',
      cursor: 'pointer',
      borderRadius: '0.5rem',
      position: 'relative',
      gap: '0.125rem',
      textAlign: 'start',
      _hover: {
        bg: 'bg.muted',
      },
      _focusVisible: siteNavigationFocusRing,
      _active: {
        bg: 'bg.emphasized',
      },
      _selected: {
        bg: 'bg.muted',
        boxShadow: `0px 1px 0px 0px ${asChakraVariable({ default: 'bg.muted' })}`,
      },
    },
    contentWrapper: {
      alignItems: 'center',
      flex: 1,
    },
    iconWrapper: {
      flex: 1,
      gap: '0.5rem',
      alignItems: 'center',
    },
    icon: {
      boxSize: '1.5rem',
      color: 'fg.muted',
      _selected: {
        color: 'fg.info',
      },
    },
    titleText: {
      ...textStyles[textStyleKeys.Body2],
      color: 'fg.muted',
      _selected: {
        color: 'fg.info',
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
      marginBottom: 0,
    },
    statusBadge: {
      zIndex: 1,
    },
  },
  sizes: {
    [SiteNavigationTabSizes.Normal]: {
      container: {
        flexDirection: 'column',
        alignSelf: 'stretch',
      },
      selectionContainer: {
        alignItems: 'center',
        alignSelf: 'stretch',
        pl: '0.75rem',
        pr: '0.5rem',
      },
    },
    [SiteNavigationTabSizes.Collapsed]: {
      selectionContainer: {
        width: '3rem',
        height: '3rem',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
      },
      notificationBadge: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(1rem, -50%)',
      },
    },
    [SiteNavigationTabSizes.CollapsedBadged]: {
      container: {
        width: '3rem',
        height: '3.5625rem',
      },
      selectionContainer: {
        flexDirection: 'column',
        px: '0.75rem',
        py: '0.625rem',
        width: '3rem',
        height: '3rem',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      },
    },
  },
  defaultProps: {
    size: SiteNavigationTabSizes.Normal,
  },
})

export const useSiteNavigationTabStyle = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(SiteNavigationTabStyle, props) as MultiStyleType<
    typeof SiteNavigationTabParts
  >
}
